import React from 'react';
import { always, cond, equals, has, isNil, map, path, pathEq, pathOr, pipe, prop, propEq, values, T } from 'ramda';
import { useIntl } from 'gatsby-plugin-intl';
import { findByName } from '../../utils/';
import { renderWhenTrue } from '../../utils/rendering';
import { PillarTable } from './pillarTable.component';
import { MethodologyTable } from './methodologyTable.component';
import {
  Container,
  Title,
  TableComponent,
  TableHeader,
  TableBody,
  Row,
  Cell,
  MarkdownContent,
  HeaderCell,
  HiddenCell,
  Footnote,
  Score,
  PillarIcon,
  PillarTitle,
} from './table.styles';
import {
  TABLES_WITH_MERGED_ROWS,
  TABLES_WITH_HIGHLIGHTED_CELL,
  TABLES_WITH_NO_HEADER,
  PRESERVE_BACKGROUND_AT_FIRST_COLUMN,
  PILLARS_ICONS,
  TABLE_IDS,
} from './table.constants';

export const Table = ({ elements }) => {
  let pillarCounter = 0;
  let customRowCounter = 0;
  let beginRowIndex = 0;
  const intl = useIntl();

  const renderTitle = ({ value }, key) =>
    renderWhenTrue(() => <Title key={key}>{intl.formatMessage({ id: value })}</Title>)(!!value);

  const renderFootnote = ({ value }, key) =>
    renderWhenTrue(() => <Footnote key={key}>{intl.formatMessage({ id: value })}</Footnote>)(!!value);

  const renderHeaderCell = (value, key) => <HeaderCell key={key}>{intl.formatMessage({ id: value })}</HeaderCell>;

  const renderCell = ({ useColor, rowContinue, pillar }) => (value, key) => (
    <Cell key={key} hasValue={!isNil(value)} useColor={useColor} rowContinue={rowContinue} pillar={pillar & !key}>
      <MarkdownContent>
        {value && intl.formatMessage({ id: value })}
      </MarkdownContent>
    </Cell>
  );

  const renderScoreCell = ({ rowIndex, rowContinue, useColor }) => (value, index) => {
    if (index) return renderCell({ useColor, rowContinue })(value, index);

    return (
      <Cell key={index} hasValue={true}>
        <Score index={rowIndex}>{value}</Score>
      </Cell>
    );
  };

  const Icon = ({ id }) => {
    if (!has(id, PILLARS_ICONS)) return null;

    const IconItem = PILLARS_ICONS[id];

    return <IconItem />;
  };

  const renderPillarCell = ({ rowContinue, useColor, rowSpans }) => (value, index) => {
    const noValue = isNil(value);
    const intl = useIntl();

    if (!index && noValue) return <HiddenCell key={index} />;

    if (index || noValue) return renderCell({ useColor, rowContinue, pillar: true })(value, index);

    const iconId = pillarCounter;
    const rowSpan = rowSpans[pillarCounter];

    pillarCounter++;

    return (
      <Cell key={index} rowSpan={rowSpan} hasValue={!noValue} pillar>
        <PillarIcon>
          <Icon id={iconId} />
        </PillarIcon>
        <PillarTitle>{intl.formatMessage({ id: value })}</PillarTitle>
      </Cell>
    );
  };

  const customRowColoring = (cell, rowIndex) => {
    if (!isNil(cell)) {
      customRowCounter++;
      beginRowIndex = rowIndex;
    }

    return !!(customRowCounter % 2);
  };

  const renderRow = (columns, tableId, rowSpans) => (row, key) => {
    const firstColumnHighlighted = TABLES_WITH_HIGHLIGHTED_CELL.includes(tableId);
    const hasMergedRows = TABLES_WITH_MERGED_ROWS.includes(tableId);
    const hasPreservedBackground = PRESERVE_BACKGROUND_AT_FIRST_COLUMN.includes(tableId);

    const cells = columns.map((columnName) => row[columnName]);
    const cellIndex = columns.length < 3 ? 0 : 1;
    const useColor = cond([
      [propEq('tableId', TABLE_IDS.SCORE), always(false)],
      [propEq('hasMergedRows', true), () => customRowColoring(cells[cellIndex], key)],
      [T, () => !(key % 2)],
    ])({ hasMergedRows, tableId });
    const rowContinue = hasMergedRows && key > beginRowIndex;

    const cellRenderer = cond([
      [equals(TABLE_IDS.SCORE), always(renderScoreCell)],
      [equals(TABLE_IDS.PILLAR), always(renderPillarCell)],
      [equals(TABLE_IDS.CRISIS_BUDGETING_PILLAR), always(renderPillarCell)],
      [equals(TABLE_IDS.SEYCHELLES_KEY_CRISIS), always(renderPillarCell)],
      [T, always(renderCell)],
    ])(tableId);

    return (
      <Row
        key={key}
        firstColumnHightlighted={firstColumnHighlighted}
        hasMergedRows={hasMergedRows}
        hasPreservedBackground={hasPreservedBackground}
        useColor={useColor}
      >
        {cells.map(cellRenderer({ rowIndex: key, useColor, rowContinue, rowSpans }))}
      </Row>
    );
  };

  const renderTable = ({ chartData } = {}, key) => {
    const rows = pipe(pathOr({}, ['records']), values)(chartData);

    const columnsIds = Object.keys(rows[0] || {});
    const tableId = path(['meta', 'id'], chartData);

    const columns = pipe(pathOr({}, ['fields']), values, map(prop('name')))(chartData);

    const hasHeader = !TABLES_WITH_NO_HEADER.includes(tableId);

    if (!hasHeader) {
      const row = {};

      columnsIds.forEach((id, index) => {
        row[id] = pathOr(id, ['fields', index, 'name'], chartData);
      });

      rows.unshift(row);
    }

    const firstColumnId = columnsIds[0];
    const rowSpans =
      tableId === TABLE_IDS.PILLAR || TABLE_IDS.CRISIS_BUDGETING_PILLAR &&
      rows.reduce((list, item) => {
        const value = item[firstColumnId];

        if (isNil(value)) {
          list[list.length - 1]++;
        } else {
          list.push(1);
        }

        return list;
      }, []);

    return (
      <TableComponent key={key} tableId={tableId}>
        {hasHeader && (
          <TableHeader tableId={tableId}>
            <Row>{columns.map(renderHeaderCell)}</Row>
          </TableHeader>
        )}
        <TableBody>{rows.map(renderRow(columnsIds, tableId, rowSpans))}</TableBody>
      </TableComponent>
    );
  };

  const tableIdEq = (id) => ({ name, chartData }) => name === 'TableName' && pathEq(['meta', 'id'], id)(chartData);

  const renderElement = cond([
    [propEq('name', 'Title'), renderTitle],
    [tableIdEq(TABLE_IDS.PILLAR), PillarTable],
    // [tableIdEq(TABLE_IDS.CRISIS_BUDGETING_PILLAR), PillarTable],
    [tableIdEq(TABLE_IDS.METHODOLOGY), MethodologyTable],
    [propEq('name', 'TableName'), renderTable],
    [propEq('name', 'Footnote'), renderFootnote],
  ]);

  const { value: type = 'short' } = findByName('Width (short or wide)', elements) || {};

  return <Container type={type}>{elements.map(renderElement)}</Container>;
};
